import {Injectable, Injector, Signal, Type, inject, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {AlertModal, AlertModalData, AlertOptions} from './alert-modal';
import {ConfirmModal, ConfirmModalData, ConfirmOptions} from './confirm-modal';
import {ModalConfig} from './modal-config.model';
import {ModalRef} from './modal-ref';

export interface ModalState<TResult = unknown, TData = unknown> {
  readonly ref: ModalRef<TResult, TData>;
  readonly componentType: Type<unknown>;
  readonly config: ModalConfig<TData>;
  readonly injector: Injector;
}

@Injectable({providedIn: 'root'})
export class ModalService {
  private readonly parentInjector = inject(Injector);
  private readonly _state = signal<ModalState | null>(null);

  readonly state: Signal<ModalState | null> = this._state.asReadonly();

  open<TComponent, TResult = unknown, TData = unknown>(
    component: Type<TComponent>,
    config: ModalConfig<TData> = {},
  ): ModalRef<TResult, TData> {
    const current = this._state();
    if (current) current.ref.close();

    const ref = new ModalRef<TResult, TData>(config.data, config.title);

    const injector = Injector.create({
      parent: this.parentInjector,
      providers: [{provide: ModalRef, useValue: ref}],
    });

    const state: ModalState<TResult, TData> = {
      ref,
      componentType: component,
      config,
      injector,
    };

    this._state.set(state as ModalState);

    // Instant close: drop the modal from the DOM the moment it closes, before
    // p-dialog can run its leave animation (whose end-of-transition class reset
    // reverted the dialog to full opacity/scale for one frame — the flicker).
    // closed$ emits once then completes, so the subscription cleans up itself.
    ref.closed$.subscribe(() => {
      if (this._state()?.ref === ref) this._state.set(null);
    });

    return ref;
  }

  /** Open the shared confirm dialog. Resolves `true` only when confirmed. */
  confirm(options: ConfirmOptions): Promise<boolean> {
    const severity = options.severity ?? 'danger';

    const data: ConfirmModalData = {
      message: options.message,
      messageParams: options.messageParams,
      confirmLabel: options.confirmLabel ?? 'common.delete',
      cancelLabel: options.cancelLabel ?? 'common.cancel',
      confirmSeverity: options.confirmSeverity ?? (severity === 'danger' ? 'danger' : 'contrast'),
    };

    const ref = this.open<ConfirmModal, boolean, ConfirmModalData>(ConfirmModal, {
      title: options.title ?? 'common.confirmDelete',
      icon: options.icon ?? 'pi pi-trash',
      severity,
      size: options.size ?? 'sm',
      data,
    });

    return firstValueFrom(ref.closed$).then((result) => result === true);
  }

  /** Open the shared acknowledge-only dialog. Resolves once the user dismisses it. */
  alert(options: AlertOptions): Promise<void> {
    const data: AlertModalData = {
      message: options.message,
      messageParams: options.messageParams,
      confirmLabel: options.confirmLabel ?? 'common.close',
    };

    const ref = this.open<AlertModal, void, AlertModalData>(AlertModal, {
      title: options.title,
      icon: options.icon,
      severity: options.severity ?? 'default',
      size: options.size ?? 'sm',
      data,
    });

    return firstValueFrom(ref.closed$).then(() => undefined);
  }
}
